import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsageContext = createContext(null);

/**
 * Centralizes /tenants/limits so widgets that need plan usage (Dashboard
 * UsageWidget, PlanLimitsCard, addon banners) share a single fetch
 * instead of hitting the backend once per component. Refreshable via
 * the exposed reload() callback.
 */
export function UsageProvider({ children }) {
  const { getAuthHeaders, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!user || !user.tenant_id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/tenants/limits`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      if (r.ok) setData(await r.json());
      else setData(null);
    } catch (e) {
      setError(e);
      setData(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenant_id]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <UsageContext.Provider value={{ data, loading, error, reload }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const ctx = useContext(UsageContext);
  if (!ctx) {
    // Lazy fallback: if no provider is mounted (e.g. public pages), return
    // a no-op shape instead of throwing, so consumer components can render
    // nothing gracefully.
    return { data: null, loading: false, error: null, reload: () => {} };
  }
  return ctx;
}
